"use client";

import { forwardRef, useEffect, useRef } from "react";
import { cn } from "~/lib/utils";

export interface AlertDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  variant?: "default" | "destructive";
}

const AlertDialog = forwardRef<HTMLDivElement, AlertDialogProps>(
  (
    {
      open,
      onOpenChange,
      title,
      description,
      confirmLabel = "Delete",
      cancelLabel = "Cancel",
      onConfirm,
      variant = "destructive",
      ...props
    },
    ref
  ) => {
    const dialogRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === "Escape" && open) {
          onOpenChange(false);
        }
      };

      if (open) {
        document.addEventListener("keydown", handleEscape);
        document.body.style.overflow = "hidden";
      }

      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "";
      };
    }, [open, onOpenChange]);

    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (
          dialogRef.current &&
          !dialogRef.current.contains(e.target as Node) &&
          open
        ) {
          onOpenChange(false);
        }
      };

      if (open) {
        document.addEventListener("mousedown", handleClickOutside);
      }

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [open, onOpenChange]);

    if (!open) return null;

    return (
      <div
        ref={ref}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        onClick={() => onOpenChange(false)}
      >
        <div
          ref={dialogRef}
          className={cn(
            "bg-white border border-[rgba(0,0,51,0.06)] rounded-lg shadow-lg",
            "flex flex-col gap-3 p-3 w-[332px]"
          )}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          {/* Text Container */}
          <div className="flex flex-col gap-1 px-3 py-0">
            {/* Heading */}
            <div className="flex items-start relative w-full">
              <p className="flex-1 font-bold leading-5 text-[#1c2024] text-sm tracking-0 whitespace-pre-wrap">
                {title}
              </p>
            </div>
            {/* Description */}
            <div className="flex items-start relative w-full">
              <p className="flex-1 font-normal leading-4 text-[#1c2024] text-xs tracking-[0.04px] whitespace-pre-wrap">
                {description}
              </p>
            </div>
          </div>

          {/* Button Container */}
          <div className="flex gap-2 items-center justify-end px-3 py-0">
            <button
              onClick={() => onOpenChange(false)}
              className="bg-[rgba(0,0,51,0.06)] h-6 px-2 rounded-[3px] flex items-center justify-center text-xs font-medium text-[#60646c] leading-4 hover:bg-[rgba(0,0,51,0.1)] transition-colors"
            >
              {cancelLabel}
            </button>
            <button
              onClick={() => {
                onConfirm();
                onOpenChange(false);
              }}
              className={cn(
                "h-6 px-2 rounded-[3px] flex items-center justify-center text-xs font-medium leading-4 transition-colors",
                variant === "destructive"
                  ? "bg-[#70161e] text-white hover:bg-[#8a1d27]"
                  : "bg-[#1c3144] text-white hover:bg-[#2a4558]"
              )}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }
);

AlertDialog.displayName = "AlertDialog";

export { AlertDialog };

